import { Component, NgModule, ViewContainerRef } from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { FieldDataUpsertOperationInterface } from '@vendasta/account-group';
import { FieldHandler, FieldHandlerValidator } from '../auxiliary-data-table/auxiliary-data-table.interface';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { ReadOnlyStyles } from './common';

export class DateFieldHandler implements FieldHandler {
  formControl: UntypedFormControl;

  constructor(
    public fieldId: string,
    public name: string,
    public label: string,
    public value: string,
    public description: string,
    public validators: FieldHandlerValidator[],
    public readOnly = false,
  ) {
    this.formControl = new UntypedFormControl(value);
    this.formControl.setValidators(validators.map((v) => v.validatorFn));
  }

  render(viewContainerRef: ViewContainerRef): void {
    viewContainerRef.clear();
    const component = viewContainerRef.createComponent<DateRendererComponent>(DateRendererComponent);
    component.instance.control = this.formControl;
    component.instance.label = this.label;
    component.instance.validators = this.validators;
    component.instance.readOnly = this.readOnly;
  }

  hasModification(): boolean {
    return this.formControl?.dirty;
  }

  upsertOperation(): FieldDataUpsertOperationInterface {
    if (this.formControl.value === null) {
      return {
        fieldDataId: this.fieldId,
        deleteValue: true,
      };
    }
    return {
      fieldDataId: this.fieldId,
      date: this.formControl.value,
    };
  }
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'MMM DD, YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'MMMM DD, YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  styles: [
    ':host, mat-form-field, input {width:100%}',
    ':host #date {margin: 0 5px; padding: 6px 0 8px;}',
    ':host #date ::ng-deep .input-and-hints {margin:0;}',
    ...ReadOnlyStyles,
  ],
  template: `
    <div *ngIf="readOnly; else formField">
      <div class="label">
        {{ label }}
      </div>
      <div class="value">
        {{ control.value ? (control.value | date: 'shortDate') : '-' }}
      </div>
    </div>
    <ng-template #formField>
      <glxy-form-field [size]="'large'" id="date">
        <glxy-label *ngIf="label">{{ label }}</glxy-label>
        <input
          matInput
          [formControl]="control"
          [matDatepicker]="datepicker"
          [placeholder]="'COMMON.ADD_FIELD' | translate: { fieldName: label?.toLowerCase() }"
        />
        <mat-datepicker-toggle matSuffix [for]="datepicker"></mat-datepicker-toggle>
        <mat-datepicker #datepicker></mat-datepicker>
        <ng-container *ngFor="let validator of validators" ngProjectAs="mat-error">
          <glxy-error *ngIf="control.hasError(validator.errorCode)">
            {{ validator.errorMessage | translate }}
          </glxy-error>
        </ng-container>
      </glxy-form-field>
    </ng-template>
  `,
  providers: [
    //for browsers with a negative GMT, the default datepicker would show the previous day
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class DateRendererComponent {
  control!: UntypedFormControl;
  label!: string;
  validators!: FieldHandlerValidator[];
  readOnly!: boolean;
}

@NgModule({
  declarations: [DateRendererComponent],
  exports: [DateRendererComponent],
  imports: [CommonModule, ReactiveFormsModule, MatDatepickerModule, TranslateModule, GalaxyFormFieldModule],
})
export class DateRendererComponentModule {}
